import { z } from "zod";
import { ROADMAP_ITEM_ID_MAX, ROADMAP_ITEM_ID_PATTERN } from "./roadmap";
/**
 * The two writes the community surface makes (`D263`): a vote on a roadmap
 * item, and a feature request. Both are the caller's, read from the access
 * token; neither path names an account.
 *
 * ── ONLINE ONLY, AND STILL SAFE TO RESEND ───────────────────────────────────
 * Neither goes through the workout's outbox (`D163`): nothing here is worth
 * queueing across a day offline, and a vote that lands hours later on an
 * item that moved on would be the wrong vote. A resend after a timeout is
 * still harmless: a vote is a PUT and a withdrawal a DELETE on the pair
 * (account, item), each answering the same state however often it runs, and
 * a request carries the client's UUIDv7, so a second POST answers the first
 * row.
 */
export const COMMUNITY_ROUTES = {
    /** `GET` — the caller's votes, as item ids. */
    votes: "/me/roadmap/votes",
    /** `PUT` votes, `DELETE` withdraws; both idempotent. */
    vote: "/me/roadmap/votes/:itemId",
    /** `POST` — one "Sugerir uma função". */
    featureRequests: "/me/feature-requests",
};
export function roadmapVotePath(itemId) {
    return `${COMMUNITY_ROUTES.votes}/${encodeURIComponent(itemId)}`;
}
/** A roadmap item's id as it travels: the curated source's own pattern. */
export const roadmapItemId = z
    .string()
    .min(1)
    .max(ROADMAP_ITEM_ID_MAX)
    .regex(ROADMAP_ITEM_ID_PATTERN);
/** `GET /me/roadmap/votes` */
export const roadmapVotesOutput = z.object({
    /** Every item the caller has a vote on, the ones no longer votable
     *  included; the app draws only those it knows. */
    itemIds: z.array(z.string()),
});
/** `PUT` and `DELETE /me/roadmap/votes/:itemId` — the state now. */
export const roadmapVoteOutput = z.object({
    itemId: z.string(),
    voted: z.boolean(),
});
/**
 * What a request is about, optionally. The CHECK on
 * `feature_request.category`; a new one deploys on the server first
 * (`D172`), and the app offers it only after.
 */
export const FEATURE_REQUEST_CATEGORIES = [
    "workout",
    "plan",
    "progress",
    "trainer",
    "other",
];
export const featureRequestCategory = z.enum(FEATURE_REQUEST_CATEGORIES);
/** The shortest request worth reading, and the longest, after trimming. */
export const FEATURE_REQUEST_TEXT_MIN = 3;
export const FEATURE_REQUEST_TEXT_MAX = 1000;
/**
 * `POST /me/feature-requests`. `id` is the client's UUIDv7, minted when the
 * form is drawn and kept across a retry — never `crypto.randomUUID()`
 * (`T-174`).
 */
export const featureRequestInput = z.strictObject({
    category: featureRequestCategory.optional(),
    id: z.uuid(),
    text: z
        .string()
        .trim()
        .min(FEATURE_REQUEST_TEXT_MIN)
        .max(FEATURE_REQUEST_TEXT_MAX),
});
/** The stored row — on a resend, the first attempt's moment. */
export const featureRequestOutput = z.object({
    id: z.uuid(),
    recordedAt: z.iso.datetime(),
});
//# sourceMappingURL=wire.js.map